// src/config/backend.ts
//
// M31 — Single source of truth for the backend address + public anon key.
//
// Before this module existed, `src/lib/supabase/client.ts` read the environment
// variables directly:
//   - import.meta.env.VITE_PUBLIC_SUPABASE_URL
//   - import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
// Every other module obtained the derived values (the Supabase client, the
// `supabaseUrl` / `supabaseAnonKey` re-exports, and `getEdgeFunctionUrl`) from
// that client module, so the raw `import.meta.env` reads lived in ONE file.
//
// This module makes that contract explicit and adds a namespace preference so
// the app keeps working unchanged while becoming portable to a new backend:
//   1. platform-neutral names win when present:
//        VITE_SUPABASE_URL · VITE_SUPABASE_ANON_KEY
//   2. otherwise fall back to the values the app uses today (the Readdy-injected
//      names):  VITE_PUBLIC_SUPABASE_URL · VITE_PUBLIC_SUPABASE_ANON_KEY
//
// Only `.env` changes to the fallback names are needed if the values are moved,
// so the current runtime value is identical (no UI / behavior change).

/** Read a possibly-undefined env value as a trimmed string ('' when absent). */
function readEnv(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

const env = import.meta.env;

// Prefer the neutral namespace, then the current (Readdy-injected) namespace.
const rawUrl =
  readEnv(env.VITE_SUPABASE_URL) || readEnv(env.VITE_PUBLIC_SUPABASE_URL);
const rawAnonKey =
  readEnv(env.VITE_SUPABASE_ANON_KEY) || readEnv(env.VITE_PUBLIC_SUPABASE_ANON_KEY);

/** Backend base URL exactly as provided (a trailing slash is preserved). */
export const BACKEND_URL = rawUrl;

/** Public anon key for the backend — safe to ship inside the client bundle. */
export const BACKEND_ANON_KEY = rawAnonKey;

/** Full URL for an Edge Function identified by its slug. */
export function getBackendFunctionUrl(functionName: string): string {
  const base = BACKEND_URL.replace(/\/+$/, '');
  return `${base}/functions/v1/${functionName}`;
}